import { useTranslation } from 'react-i18next'

import { ApiError } from '@/lib/apiClient'

/**
 * Les permissions qu'un refus de délégation énumère.
 *
 * Le serveur refuse de donner ce qu'on ne détient pas (attribuer un rôle,
 * composer un rôle) et range les codes manquants dans `details[].not_held`.
 * On les lit ici, une fois, pour les deux écrans qui en ont besoin.
 */
export function notHeldPermissions(error: unknown): string[] {
  if (!(error instanceof ApiError)) return []
  const codes: string[] = []
  for (const detail of error.details) {
    const value = detail['not_held']
    if (Array.isArray(value)) {
      for (const code of value) if (typeof code === 'string') codes.push(code)
    }
  }
  return codes
}

/**
 * Le message d'une écriture refusée, et la liste de ce qui manque.
 *
 * Sans la liste, « vous ne pouvez pas attribuer ce rôle » laisse chercher au
 * hasard ce qui coince ; avec elle, on sait qui aller voir et pour quoi.
 * `describe` traduit un code en phrase quand l'écran connaît le catalogue.
 */
export function RbacError({
  error,
  describe,
}: {
  error: unknown
  describe?: (code: string) => string | undefined
}) {
  const { t } = useTranslation()
  if (!(error instanceof ApiError)) return null
  const missing = notHeldPermissions(error)

  return (
    <div className="mt-4 rounded-md border border-state-danger/40 px-3 py-3 text-sm text-state-danger">
      <p>{error.message}</p>
      {missing.length ? (
        <>
          <p className="mt-2 text-xs text-content-secondary">{t('admin.roles.notHeldList')}</p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {missing.map((code) => {
              const label = describe?.(code)
              return (
                <li key={code} className="break-words">
                  <span className="font-mono">{code}</span>
                  {label ? <span className="text-content-muted"> — {label}</span> : null}
                </li>
              )
            })}
          </ul>
        </>
      ) : null}
    </div>
  )
}
